import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Alert,
  Chip,
  Card,
  CardContent,
  Grid,
  LinearProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Switch,
  FormControlLabel
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import CloudDownloadIcon from '@mui/icons-material/CloudDownload';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import SyncIcon from '@mui/icons-material/Sync';
import { ref, get } from 'firebase/database';
import { database } from '../../firebase';
import { useAuth } from '../../contexts/AuthContext';
import { authApi } from '../../config/apiClient';
import SuperAdminLayout from './SuperAdminLayout';
import GuideButton from '../common/guide/GuideButton';

interface MigrationStats {
  totalUsers: number;
  activeUsers: number;
  migratedFromFirebase: number;
  passwordCompleted: number;
  passwordPending: number;
  passwordCompletionRate: number;
  last30DaysActiveUsers: number;
  roleDistribution: { role: string; count: number }[];
}

interface FirebaseUser {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  role: string;
  companyId?: string;
  companyName?: string;
  branchesId?: string;
  employeeId?: string;
  profileImageUrl?: string;
}

const SuperAdminUserMigration: React.FC = () => {
  const { userDetails, isSuperAdmin } = useAuth();
  const [loading, setLoading] = useState(false);
  const [stats, setStats] = useState<MigrationStats | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  
  // Firebase users
  const [firebaseUsers, setFirebaseUsers] = useState<FirebaseUser[]>([]);
  const [loadingFirebase, setLoadingFirebase] = useState(false);
  
  // Import dialog
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [importing, setImporting] = useState(false);
  const [overwriteExisting, setOverwriteExisting] = useState(false);
  const [importResult, setImportResult] = useState<any>(null);

  // JSON upload
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Migration istatistiklerini yükle
  const loadStats = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await authApi.get('/api/auth/migration-stats');
      setStats(response.data.stats);
    } catch (err) {
      setError('API bağlantı hatası');
      console.error('Stats error:', err);
    } finally {
      setLoading(false);
    }
  };

  // Firebase'den kullanıcıları yükle (direkt erişim - kural izni gerektirir)
  const loadFirebaseUsers = async () => {
    setLoadingFirebase(true);
    setError(null);
    try {
      const usersRef = ref(database, 'users');
      const snapshot = await get(usersRef);
      
      if (snapshot.exists()) {
        const usersData = snapshot.val();
        parseAndSetUsers(usersData);
      } else {
        setFirebaseUsers([]);
        setError('Firebase\'de kullanıcı bulunamadı');
      }
    } catch (err: any) {
      if (err.message?.includes('Permission denied')) {
        setError('Firebase erişim izni yok. Lütfen JSON dosyası yükleyin.');
      } else {
        setError('Firebase bağlantı hatası: ' + err.message);
      }
      console.error('Firebase error:', err);
    } finally {
      setLoadingFirebase(false);
    }
  };

  // JSON dosyasından kullanıcıları yükle
  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const jsonData = JSON.parse(e.target?.result as string);
        
        // Firebase export formatını kontrol et
        // Eğer "users" key'i varsa, onun içindeki veriyi al
        const usersData = jsonData.users || jsonData;
        
        parseAndSetUsers(usersData);
        setSuccess(`JSON dosyasından ${firebaseUsers.length} kullanıcı yüklendi`);
      } catch (err) {
        setError('JSON dosyası parse edilemedi. Geçerli bir JSON dosyası yükleyin.');
        console.error('JSON parse error:', err);
      }
    };
    reader.readAsText(file);
    
    // Input'u sıfırla (aynı dosyayı tekrar yükleyebilmek için)
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // Kullanıcı verilerini parse et ve state'e kaydet
  const parseAndSetUsers = (usersData: any) => {
    const usersList: FirebaseUser[] = [];
    
    Object.entries(usersData).forEach(([uid, userData]: [string, any]) => {
      // Sadece sistem kullanıcılarını al (admin, manager, employee, courier)
      const role = userData.role || '';
      if (['admin', 'manager', 'employee', 'courier', 'superadmin'].includes(role)) {
        usersList.push({
          id: uid,
          email: userData.email || '',
          firstName: userData.firstName || '',
          lastName: userData.lastName || '',
          role: role,
          companyId: userData.companyId || '',
          companyName: userData.companyName || '',
          branchesId: userData.branchesId || '',
          employeeId: userData.employeeId || '',
          profileImageUrl: userData.profileImageUrl || ''
        });
      }
    });
    
    setFirebaseUsers(usersList);
    setSuccess(`${usersList.length} sistem kullanıcısı bulundu (admin, manager, employee, courier, superadmin)`);
  };

  // Toplu import
  const handleImport = async () => {
    if (!isSuperAdmin) {
      setError('Bu işlem için yetkiniz yok!');
      return;
    }

    setImporting(true);
    setError(null);
    setImportResult(null);

    try {
      // Email ve FirebaseUid'si olan kullanıcıları filtrele
      const validUsers = firebaseUsers.filter(user => user.id && user.email && user.role);
      
      if (validUsers.length === 0) {
        setError('Import edilecek geçerli kullanıcı bulunamadı (email ve role zorunlu)');
        setImporting(false);
        return;
      }

      const usersToImport = validUsers.map(user => ({
        FirebaseUid: user.id,
        Email: user.email,
        FirstName: user.firstName || null,
        LastName: user.lastName || null,
        Role: user.role,
        CompanyId: user.companyId || null,
        CompanyName: user.companyName || null,
        BranchesId: user.branchesId || null,
        EmployeeId: user.employeeId || null,
        ProfileImageUrl: user.profileImageUrl || null
      }));

      const payload = {
        RequestedByEmail: userDetails.email,
        OverwriteExisting: overwriteExisting,
        Users: usersToImport
      };

      console.log('📤 [Import] Gönderilen payload:', payload);
      console.log('📤 [Import] Users sayısı:', usersToImport.length);

      const response = await authApi.post('/api/auth/import-firebase', payload);
      const result = response.data;
      console.log('📥 [Import] Response:', result);
      
      setImportResult(result);
      setSuccess(`Import tamamlandı! ${result.stats.created} oluşturuldu, ${result.stats.updated} güncellendi`);
      loadStats();
    } catch (err) {
      setError('Import sırasında hata oluştu');
      console.error('Import error:', err);
    } finally {
      setImporting(false);
      setImportDialogOpen(false);
    }
  };

  useEffect(() => {
    loadStats();
  }, []);

  const getRoleColor = (role: string) => {
    switch (role) {
      case 'superadmin': return 'error';
      case 'admin': return 'primary';
      case 'manager': return 'secondary';
      case 'employee': return 'success';
      case 'courier': return 'warning';
      default: return 'default';
    }
  };

  return (
    <SuperAdminLayout title="User Migration" icon={<SyncIcon />}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
        <Typography variant="body2" color="text.secondary">
          Firebase'den SQL'e kullanıcı migrasyonu yönetimi
        </Typography>
        <GuideButton guideKey="superadmin.userMigration" size="small" />
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      {/* İstatistik Kartları */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="text.secondary" gutterBottom>
                Toplam Kullanıcı
              </Typography>
              <Typography variant="h4">
                {loading ? <CircularProgress size={24} /> : stats?.totalUsers || 0}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="text.secondary" gutterBottom>
                Şifre Tamamlanan
              </Typography>
              <Typography variant="h4" color="success.main">
                {loading ? <CircularProgress size={24} /> : stats?.passwordCompleted || 0}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="text.secondary" gutterBottom>
                Şifre Bekleyen
              </Typography>
              <Typography variant="h4" color="warning.main">
                {loading ? <CircularProgress size={24} /> : stats?.passwordPending || 0}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="text.secondary" gutterBottom>
                Tamamlanma Oranı
              </Typography>
              <Typography variant="h4" color="primary.main">
                {loading ? <CircularProgress size={24} /> : `%${stats?.passwordCompletionRate || 0}`}
              </Typography>
              {stats && (
                <LinearProgress 
                  variant="determinate" 
                  value={stats.passwordCompletionRate} 
                  sx={{ mt: 1 }}
                />
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Rol Dağılımı */}
      {stats?.roleDistribution && stats.roleDistribution.length > 0 && (
        <Paper sx={{ p: 2, mb: 3 }}>
          <Typography variant="h6" gutterBottom>Rol Dağılımı</Typography>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            {stats.roleDistribution.map((item) => (
              <Chip
                key={item.role}
                label={`${item.role}: ${item.count}`}
                color={getRoleColor(item.role) as any}
                variant="outlined"
              />
            ))}
          </Box>
        </Paper>
      )}

      {/* Aksiyon Butonları */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Typography variant="h6" gutterBottom>İşlemler</Typography>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={loadStats}
            disabled={loading}
          >
            İstatistikleri Yenile
          </Button>
          
          {/* Gizli file input */}
          <input
            type="file"
            accept=".json"
            ref={fileInputRef}
            style={{ display: 'none' }}
            onChange={handleFileUpload}
          />
          
          <Button
            variant="outlined"
            color="secondary"
            startIcon={<UploadFileIcon />}
            onClick={() => fileInputRef.current?.click()}
          >
            JSON Dosyası Yükle
          </Button>
          
          <Button
            variant="outlined"
            startIcon={<CloudDownloadIcon />}
            onClick={loadFirebaseUsers}
            disabled={loadingFirebase}
          >
            {loadingFirebase ? 'Yükleniyor...' : 'Firebase\'den Çek (Kural izni gerekir)'}
          </Button>
          
          <Button
            variant="contained"
            color="primary"
            startIcon={<CloudUploadIcon />}
            onClick={() => setImportDialogOpen(true)}
            disabled={firebaseUsers.length === 0 || !isSuperAdmin}
          >
            SQL'e Toplu Import ({firebaseUsers.length})
          </Button>
        </Box>
        
        {!isSuperAdmin && (
          <Alert severity="warning" sx={{ mt: 2 }}>
            Toplu import yalnızca süper admin yetkisine sahip kullanıcılar tarafından yapılabilir.
          </Alert>
        )}
      </Paper>

      {/* Firebase Kullanıcıları Tablosu */}
      {firebaseUsers.length > 0 && (
        <Paper sx={{ p: 2 }}>
          <Typography variant="h6" gutterBottom>
            Firebase Kullanıcıları ({firebaseUsers.length})
          </Typography>
          <TableContainer sx={{ maxHeight: 400 }}>
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Email</TableCell>
                  <TableCell>Ad Soyad</TableCell>
                  <TableCell>Rol</TableCell>
                  <TableCell>Şirket</TableCell>
                  <TableCell>Firebase UID</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {firebaseUsers.map((user) => (
                  <TableRow key={user.id} hover>
                    <TableCell>{user.email}</TableCell>
                    <TableCell>{user.firstName} {user.lastName}</TableCell>
                    <TableCell>
                      <Chip 
                        label={user.role} 
                        size="small" 
                        color={getRoleColor(user.role) as any}
                      />
                    </TableCell>
                    <TableCell>{user.companyName || '-'}</TableCell>
                    <TableCell>
                      <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>
                        {user.id.substring(0, 12)}...
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      {/* Import Onay Dialog */}
      <Dialog open={importDialogOpen} onClose={() => setImportDialogOpen(false)}>
        <DialogTitle>Toplu Import Onayı</DialogTitle>
        <DialogContent>
          <Typography sx={{ mb: 2 }}>
            <strong>{firebaseUsers.length}</strong> kullanıcı SQL veritabanına aktarılacak.
          </Typography>
          <Alert severity="info" sx={{ mb: 2 }}>
            Bu işlem kullanıcıları <strong>şifresiz</strong> olarak ekler. 
            Kullanıcılar login olduğunda şifreleri otomatik güncellenecektir.
          </Alert>
          <FormControlLabel
            control={
              <Switch
                checked={overwriteExisting}
                onChange={(e) => setOverwriteExisting(e.target.checked)}
              />
            }
            label="Mevcut kayıtları güncelle (şifre hariç)"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setImportDialogOpen(false)}>İptal</Button>
          <Button 
            onClick={handleImport} 
            variant="contained" 
            color="primary"
            disabled={importing}
          >
            {importing ? <CircularProgress size={24} /> : 'Import Et'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Import Sonucu */}
      {importResult && (
        <Alert severity="success" sx={{ mt: 2 }}>
          <Typography variant="subtitle1">Import Sonucu:</Typography>
          <ul>
            <li>Toplam: {importResult.stats.total}</li>
            <li>Oluşturulan: {importResult.stats.created}</li>
            <li>Güncellenen: {importResult.stats.updated}</li>
            <li>Atlanan: {importResult.stats.skipped}</li>
            {importResult.stats.errors > 0 && (
              <li style={{ color: 'red' }}>Hatalar: {importResult.stats.errors}</li>
            )}
          </ul>
        </Alert>
      )}
    </SuperAdminLayout>
  );
};

export default SuperAdminUserMigration;
